import { useState } from 'react';
import { ArrowLeft, Copy, History } from 'lucide-react';
import {
  DashboardPageFormBody,
  DashboardPageFormShell,
  DashboardPageMetadataItem,
} from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { copyToClipboard } from '../../../../../lib/clipboard';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { dashboardErrorMessage } from '../../../../../lib/apiErrorMessages';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { useIntegrationMutations, useIntegrationQuery } from '../../hooks';
import { useStandaloneTargetingClient } from '../../../../targeting/builder/hooks';
import {
  emptyIntegrationFormValue,
  type IntegrationFormValue,
  type IntegrationSaveOutcome,
} from '../../integrationFormSchema';
import { integrationToFormValue } from '../../integrationFormModel';
import { saveIntegrationWithTargetingCsv } from '../../saveIntegrationWithTargetingCsv';
import { IntegrationPlacementsDialog } from '../IntegrationPlacementsDialog';
import { IntegrationEditorForm } from './IntegrationEditorForm';

export function IntegrationEditorPage(props: {
  demandPartnerId?: string | undefined;
  integrationId?: string | undefined;
  copyFromIntegrationId?: string | undefined;
}) {
  const { returnTo } = useReturnNavigation({ to: '/demand/integrations' });
  const { can } = useShigoPermissions();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [placementsOpen, setPlacementsOpen] = useState(false);
  const [createdIntegrationId, setCreatedIntegrationId] = useState('');
  const effectiveIntegrationId = props.integrationId ?? createdIntegrationId;
  const editing = Boolean(effectiveIntegrationId);
  const sourceIntegrationId = props.integrationId ?? props.copyFromIntegrationId ?? '';
  const query = useIntegrationQuery(props.demandPartnerId ?? '', sourceIntegrationId);
  const headerIntegration =
    props.integrationId && query.isFetchedAfterMount && !query.isError ? query.data : undefined;
  const canReadChangelog = can(ShigoPermission.DemandIntegrationsChangelogRead);
  const mutations = useIntegrationMutations();
  const targetingClient = useStandaloneTargetingClient();
  const [importingCsv, setImportingCsv] = useState(false);
  const saving =
    mutations.create.isPending ||
    mutations.update.isPending ||
    mutations.get.isPending ||
    importingCsv;
  const initialValue = query.data
    ? integrationToFormValue(query.data)
    : {
        ...emptyIntegrationFormValue,
        demandPartnerId: props.demandPartnerId ?? '',
      };

  const headerIntegrationId = headerIntegration?.id ?? createdIntegrationId;

  async function copyIntegrationId() {
    if (!headerIntegrationId) return;
    await copyToClipboard(headerIntegrationId, {
      successMessage: dashboardT('integrations.idsCopied', { count: 1 }),
      errorMessage: dashboardT('integrations.idsCopyError'),
    });
  }

  async function save(value: IntegrationFormValue): Promise<IntegrationSaveOutcome | undefined> {
    setImportingCsv(Boolean(value.pendingBundleCsv || value.pendingIpCsv));
    try {
      const result = await saveIntegrationWithTargetingCsv({
        value,
        integrationId: effectiveIntegrationId,
        onIntegrationIdResolved: (integrationId) => {
          if (!effectiveIntegrationId) setCreatedIntegrationId(integrationId);
        },
        dependencies: {
          create: async (demandPartnerId, payload) => {
            const created = await mutations.create.mutateAsync({ demandPartnerId, payload });
            return created ?? undefined;
          },
          update: (demandPartnerId, integrationId, payload) =>
            mutations.update.mutateAsync({ demandPartnerId, integrationId, payload }),
          get: (demandPartnerId, integrationId) =>
            mutations.get.mutateAsync({ demandPartnerId, integrationId }),
          importBundlesCsv: targetingClient.importBundlesCsv,
          importIpsCsv: targetingClient.importIpsCsv,
        },
      });
      if (result.csvFailed) {
        toast.error(dashboardT('integrations.targetingCsvPartialError'), {
          description:
            result.csvError === undefined ? undefined : dashboardErrorMessage(result.csvError),
        });
        return result.outcome;
      }
      toast.success(
        dashboardT(editing ? 'integrations.updatedSuccess' : 'integrations.createdSuccess'),
      );
      await returnTo({ replace: true });
      return result.outcome;
    } catch (error) {
      showRequestError(error, dashboardT('integrations.saveError'));
      return;
    } finally {
      setImportingCsv(false);
    }
  }

  let content = null;
  const loadingSource =
    Boolean(sourceIntegrationId) && !query.isFetchedAfterMount && !query.isError;
  if (loadingSource) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('integrations.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  } else if (sourceIntegrationId && query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('integrations.detailsError')}>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('common.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else {
    content = (
      <IntegrationEditorForm
        key={props.integrationId ?? props.copyFromIntegrationId ?? 'create'}
        initialValue={initialValue}
        initialIntegration={query.data}
        editing={editing}
        saving={saving}
        onCancel={() => void returnTo()}
        onSubmit={save}
      />
    );
  }

  return (
    <>
      <DashboardPageFormShell
        title={dashboardT(editing ? 'integrations.editTitle' : 'integrations.createTitle')}
        headerActions={
          headerIntegration && canReadChangelog ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setChangelogOpen(true);
              }}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              {dashboardT('integrations.changelog')}
            </Button>
          ) : undefined
        }
        secondaryRow={
          headerIntegration || createdIntegrationId ? (
            <>
              <DashboardPageMetadataItem
                label={dashboardT('integrations.integrationId')}
                value={
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate" title={headerIntegrationId}>
                      {headerIntegrationId || '-'}
                    </span>
                    {headerIntegrationId ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 shrink-0"
                        aria-label={dashboardT('integrations.copyId')}
                        onClick={() => void copyIntegrationId()}
                      >
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </span>
                }
              />
              <DashboardPageMetadataItem
                label={dashboardT('integrations.connectedPlacements')}
                value={
                  headerIntegration?.id && headerIntegration.placementCnt ? (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0"
                      aria-label={dashboardT('integrations.viewConnectedPlacements', {
                        count: headerIntegration.placementCnt,
                      })}
                      onClick={() => {
                        setPlacementsOpen(true);
                      }}
                    >
                      {headerIntegration.placementCnt}
                    </Button>
                  ) : (
                    (headerIntegration?.placementCnt ?? '-')
                  )
                }
              />
            </>
          ) : undefined
        }
        backAction={
          saving ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              aria-label={dashboardT('integrations.back')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : (
            <ReturnBackLink
              fallback={{ to: '/demand/integrations' }}
              label={dashboardT('integrations.back')}
            />
          )
        }
      >
        {content}
      </DashboardPageFormShell>
      {headerIntegration?.id && placementsOpen ? (
        <IntegrationPlacementsDialog
          integration={headerIntegration}
          onOpenChange={setPlacementsOpen}
        />
      ) : null}
      {headerIntegration && canReadChangelog && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('integrations.changelogTitle', { name: headerIntegration.name })}
          description={dashboardT('integrations.changelogDescription')}
          scope={{ integrationIds: [headerIntegration.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
